import { Check } from 'lucide-react'
import { Typography } from '@/components/ui/typography'
import { useRescreenDealLead } from '@/hooks/use-deal-leads'
import { showError, showInfo } from '@/lib/toast'
import { isRescreening } from '@/features/deal-review/deal-lead-views'
import { PROCESSING_STAGES } from '@/features/deal-review/deal-lead-labels'
import { cn } from '@/lib/utils'
import type { DealLeadDetail } from '@/types/api'

// Motion is CSS only: no timers or per-frame renders on a panel that may sit open.
export function LeadProcessingProgress({ lead }: { lead: DealLeadDetail }) {
  // A re-screen runs only the last stage.
  const screening = isRescreening(lead)
  const done = new Set<string>(
    screening
      ? PROCESSING_STAGES.slice(0, -1).map((stage) => stage.id)
      : lead.message.completedStages
  )
  const current = PROCESSING_STAGES.find((stage) => !done.has(stage.id))

  return (
    <div className="rounded-lg border bg-gradient-to-b from-muted/30 to-transparent p-5">
      <Typography as="p" variant="body" className="font-medium">
        {screening ? 'Screening this lead again' : 'Working through this email'}
      </Typography>
      <Typography
        as="p"
        variant="body-sm"
        className="mt-1 text-muted-foreground"
      >
        {screening
          ? 'The new verdict will appear here when it finishes.'
          : 'The figures and the verdict will appear here when it finishes.'}
      </Typography>

      <ol className="mt-5">
        {PROCESSING_STAGES.map((stage, index) => {
          const complete = done.has(stage.id)
          const active = stage.id === current?.id
          const last = index === PROCESSING_STAGES.length - 1

          return (
            <li
              key={stage.id}
              className="flex animate-in gap-3 fade-in slide-in-from-bottom-1 fill-mode-backwards"
              style={{ animationDelay: `${index * 60}ms` }}
            >
              <div className="flex flex-col items-center">
                <span
                  className={cn(
                    'relative flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors duration-500',
                    complete && 'border-emerald-600 bg-emerald-600 text-white',
                    active && 'border-sky-600/30',
                    !complete && !active && 'border-muted-foreground/25'
                  )}
                >
                  {complete && (
                    <Check
                      aria-hidden="true"
                      strokeWidth={3}
                      className="size-3 animate-in zoom-in duration-300"
                    />
                  )}
                  {active && (
                    <span className="absolute inset-0 animate-spin rounded-full border-2 border-transparent border-t-sky-600 duration-1000" />
                  )}
                  {active && (
                    <span className="size-1.5 rounded-full bg-sky-600" />
                  )}
                </span>

                {!last && (
                  <span
                    className={cn(
                      'my-0.5 w-px flex-1 transition-colors duration-500',
                      complete ? 'bg-emerald-600/40' : 'bg-muted-foreground/15'
                    )}
                  />
                )}
              </div>

              <Typography
                as="span"
                variant="body-sm"
                className={cn(
                  'pb-4 transition-colors duration-500',
                  complete && 'text-muted-foreground',
                  active && 'font-medium text-foreground',
                  !complete && !active && 'text-muted-foreground/50'
                )}
              >
                {stage.label}
              </Typography>
            </li>
          )
        })}
      </ol>

      {screening && <RescreenEscapeHatch leadId={lead.id} />}
    </div>
  )
}

// A re-screen whose job died leaves the lead stuck busy; the server refuses this mid-run.
function RescreenEscapeHatch({ leadId }: { leadId: string }) {
  const rescreen = useRescreenDealLead(leadId)

  return (
    <Typography as="p" variant="body-sm" className="mt-4 text-muted-foreground">
      Taking longer than it should?{' '}
      <button
        type="button"
        className="cursor-pointer underline underline-offset-2 hover:text-foreground disabled:cursor-default disabled:opacity-60"
        disabled={rescreen.isPending}
        onClick={() =>
          rescreen.mutate(leadId, {
            onSuccess: () => showInfo('Asked for it again.'),
            onError: (error) =>
              showError(error, 'This lead could not be screened again.'),
          })
        }
      >
        Start it again
      </button>
    </Typography>
  )
}
